import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../constants/config';
import '../styles/Blog.css';

export default function Blog() {
  const location = useLocation();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [article, setArticle] = useState(null);
  const [authorDetails, setAuthorDetails] = useState(null);
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]); // array of URLs
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Lightbox controls (component scope)
  const closeLightbox = () => setLightboxOpen(false);
  const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
  const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxImages.length]);

  const queryParams = new URLSearchParams(location.search);
  const articleId = queryParams.get('id');

  useEffect(() => {
    const fetchArticle = async () => {
      // Support both slug from URL params and id from query params
      if (!articleId && !slug) {
        setError('No article ID or slug provided');
        setLoading(false);
        return;
      }
      try {
        // Build request payload - use slug if available, otherwise use id
        const payload = slug ? { slug } : { id: Number(articleId) };
        
        const res = await axios.post(
          `${config.api.url}articles/get`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'api': config.api.key,
            },
          }
        );
        const art = res?.data?.data || null;
        setArticle(art);
        // Fetch author details from articles_authors table
        try {
          // Use the article ID from the fetched article (works for both slug and id routes)
          const fetchedArticleId = art?.id;
          if (!fetchedArticleId) {
            setAuthorDetails(null);
            return;
          }
          
          const ares = await axios.post(
            `${config.api.url}articles/authors/get`,
            { article_id: Number(fetchedArticleId) },
            {
              headers: {
                'Content-Type': 'application/json',
                'api': config.api.key,
              },
            }
          );
          const a = ares?.data?.data || ares?.data || null;
          // Accept shapes: { name, subtitle, image, location_from, location_current, traveller_type, twitter_url, instagram_url }
          if (a && (a.name || a.subtitle || a.image)) {
            setAuthorDetails({ 
              name: a.name || '', 
              subtitle: a.subtitle || '', 
              image: a.image || '',
              location_from: a.location_from || '',
              location_current: a.location_current || '',
              traveller_type: a.traveller_type || '',
              twitter_url: a.twitter_url || '',
              instagram_url: a.instagram_url || ''
            });
          } else if (a && a.author) {
            setAuthorDetails(a.author);
          } else {
            setAuthorDetails(null);
          }
        } catch (e) {
          // Non-fatal if author not found
          setAuthorDetails(null);
        }
      } catch (e) {
        setError(e.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId, slug]);

  const formatDate = (unix) => {
    if (!unix) return '';
    try { return new Date(unix * 1000).toLocaleDateString(); } catch { return ''; }
  };

  const renderBlocks = (blocks) => {
    if (!Array.isArray(blocks)) return null;
    return blocks.map((b, idx) => {
      if (b.type === 'header') {
        return (
          <h2 key={idx} className="blog-block-header">
            {b.content}
          </h2>
        );
      }
      if (b.type === 'image') {
        const c = b.content;
        // Possible shapes: string URL, or { images: [{ url, caption }] }
        if (typeof c === 'string') {
          return (
            <div key={idx} className="blog-image-item">
              <img
                src={c}
                alt=""
                className="blog-block-image blog-image-clickable"
                onClick={() => {
                  setLightboxImages([c]);
                  setLightboxIndex(0);
                  setLightboxOpen(true);
                }}
              />
            </div>
          );
        }
        if (c && Array.isArray(c.images)) {
          const images = c.images;
          const Wrapper = ({ children }) => (
            <div className={images.length > 1 ? 'blog-images-grid' : ''}>{children}</div>
          );
          return (
            <Wrapper key={idx}>
              {images.map((im, i) => (
                <div key={`img-${idx}-${i}`} className="blog-image-item">
                  <img
                    src={im.url}
                    alt={im.caption || ''}
                    className="blog-block-image blog-image-clickable"
                    onClick={() => {
                      const urls = images.map(m => m.url);
                      setLightboxImages(urls);
                      setLightboxIndex(i);
                      setLightboxOpen(true);
                    }}
                  />
                  {im.caption && (
                    <div className="blog-image-caption">{im.caption}</div>
                  )}
                </div>
              ))}
            </Wrapper>
          );
        }
        return null;
      }
      // default paragraph/text (Draft.js raw stored as JSON string or HTML from TipTap)
      if (b.type === 'text') {
        const c = b.content;
        try {
          // Check if it's HTML content from TipTap
          if (typeof c === 'string' && (c.startsWith('<') || c.includes('<p>') || c.includes('<h'))) {
            return (
              <div 
                key={idx} 
                className="blog-block-html"
                dangerouslySetInnerHTML={{ __html: c }}
              />
            );
          }
          
          // When content is a JSON string of Draft.js raw
          const raw = typeof c === 'string' ? JSON.parse(c) : c;
          if (raw && Array.isArray(raw.blocks)) {
            return (
              <div key={idx}>
                {raw.blocks.map((blk, bi) => {
                  // Render based on block type
                  switch (blk.type) {
                    case 'header-one':
                      return <h1 key={`h1-${idx}-${bi}`} className="blog-block-h1">{blk.text}</h1>;
                    case 'header-two':
                      return <h2 key={`h2-${idx}-${bi}`} className="blog-block-h2">{blk.text}</h2>;
                    case 'header-three':
                      return <h3 key={`h3-${idx}-${bi}`} className="blog-block-h3">{blk.text}</h3>;
                    case 'header-four':
                      return <h4 key={`h4-${idx}-${bi}`} className="blog-block-h4">{blk.text}</h4>;
                    case 'header-five':
                      return <h5 key={`h5-${idx}-${bi}`} className="blog-block-h5">{blk.text}</h5>;
                    case 'header-six':
                      return <h6 key={`h6-${idx}-${bi}`} className="blog-block-h6">{blk.text}</h6>;
                    case 'blockquote':
                      return <blockquote key={`bq-${idx}-${bi}`} className="blog-block-blockquote">{blk.text}</blockquote>;
                    case 'unordered-list-item':
                      return <li key={`li-${idx}-${bi}`} className="blog-block-li">{blk.text}</li>;
                    case 'ordered-list-item':
                      return <li key={`li-${idx}-${bi}`} className="blog-block-li">{blk.text}</li>;
                    default:
                      return <p key={`p-${idx}-${bi}`} className="blog-block-paragraph">{blk.text}</p>;
                  }
                })}
              </div>
            );
          }
        } catch (_) {
          // Fallback to plain paragraph if not JSON
        }
        return (
          <p key={idx} className="blog-block-paragraph">
            {typeof c === 'string' ? c : ''}
          </p>
        );
      }
      // Unknown block type: ignore
      return null;
    });
  };

  if (loading) return <div className="blog-loading">Loading...</div>;
  if (error) return <div className="blog-error">{error}</div>;
  if (!article) return <div className="blog-not-found">Article not found.</div>;

  return (
    <div className="blog-container">
      {/* Hero Image */}
      {article.featured_image_url && (
        <img
          src={article.featured_image_url}
          alt={article.title}
          className="blog-hero"
        />
      )}

      {/* Title */}
      <h1 className="blog-title">{article.title}</h1>

      {/* Meta */}
      <div className="blog-meta">
        {article.category && <span className="blog-category">{article.category}</span>}
        {article.author && <span style={{ marginRight: '15px' }}>By {article.author}</span>}
        {(article.author || article.date_published) && <span className="blog-meta-sep">|</span>}
        {article.date_published && <span>{formatDate(article.date_published)}</span>}
      </div>

      {/* Excerpt */}
      {article.excerpt && (
        <div className="blog-excerpt">{article.excerpt}</div>
      )}

      {/* Content / Blocks */}
      <div className="blog-blocks">
        {renderBlocks(article.blocks)}
      </div>

      {/* Tags */}
      {Array.isArray(article.tags) && article.tags.length > 0 && (
        <div style={{ marginTop: '30px', padding: '0 175px' }}>
          <div style={{ fontWeight: 600, marginBottom: '5px', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>Tags:</div>
          <div>
            {article.tags.map((tag, i) => (
              <span key={`${tag}-${i}`} style={{ display: 'inline-block', background: '#e0e0e0', padding: '3px 8px', borderRadius: '4px', margin: '0 5px 5px 0', fontSize: '14px', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Author Block at bottom */}
      {authorDetails && (authorDetails.name || authorDetails.image) && (
        <div className="blog-author">
          {authorDetails.image ? (
            <img src={authorDetails.image} alt={authorDetails.name || 'Author'} className="blog-author-avatar" />
          ) : null}
          <div className="blog-author-meta">
            {authorDetails.name ? (
              <div className="blog-author-name">{authorDetails.name}</div>
            ) : null}
            {authorDetails.subtitle ? (
              <div className="blog-author-role">{authorDetails.subtitle}</div>
            ) : null}
            {(authorDetails.location_from || authorDetails.location_current || authorDetails.traveller_type) && (
              <div className="blog-author-details">
                {authorDetails.location_from && `From: ${authorDetails.location_from}`}
                {authorDetails.location_from && authorDetails.location_current && ' | '}
                {authorDetails.location_current && `From, from: ${authorDetails.location_current}`}
                {(authorDetails.location_from || authorDetails.location_current) && authorDetails.traveller_type && ' | '}
                {authorDetails.traveller_type && `Traveller type: ${authorDetails.traveller_type}`}
              </div>
            )}
            {(authorDetails.twitter_url || authorDetails.instagram_url) && (
              <div className="blog-author-socials">
                {authorDetails.twitter_url && (
                  <a href={authorDetails.twitter_url} target="_blank" rel="noopener noreferrer" className="blog-author-social-link">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                )}
                {authorDetails.instagram_url && (
                  <a href={authorDetails.instagram_url} target="_blank" rel="noopener noreferrer" className="blog-author-social-link">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Lightbox Overlay */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div className="lightbox-backdrop" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
            {lightboxImages.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
              </>
            )}
            <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  );
}
